'use client'

import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react/dist/ssr'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'

import { CONTENTFUL_ASSETS } from '@/lib/contentful-assets.constants'
import { cn } from '@/lib/utils'

import { Banner } from './Banner'

import type { HeroBannerType } from '@/types/contentful/heroBannerType'

export const HeroBanner = ({ slides, bannerSize }: HeroBannerType) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, inViewThreshold: 0.1 })
  const [currentSlide, setCurrentSlide] = useState(0)
  const resolvedBannerSize: '708' | '516' = bannerSize === '516' ? '516' : '708'
  const bannerSizeStyles = {
    '708': 'min-h-[380px] md:min-h-[584px] desktop:min-h-[708px]',
    '516': 'min-h-[380px] md:min-h-[356px] desktop:min-h-[516px]',
  }

  const handleThumbnailClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index)
    },
    [emblaApi],
  )

  // currentSlide is a one-way mirror of Embla's state; never drive Embla back from it.
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => setCurrentSlide(emblaApi.selectedScrollSnap())
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi])

  if (!slides?.length) return null

  if (slides.length === 1) {
    const hasOverlay = slides[0]?.fields?.enableShapeOverlay
    const singleSlideHeight = hasOverlay
      ? bannerSizeStyles[resolvedBannerSize]
      : `min-h-[320px] h-auto ${resolvedBannerSize === '708' ? 'desktop:min-h-[708px]' : 'desktop:min-h-[516px]'}`

    return (
      <section className={cn('relative w-full', singleSlideHeight)}>
        <Image src={CONTENTFUL_ASSETS.HERO_BACKGROUND} alt="Hero background" fill sizes="100vw" className="object-top-center -z-10 object-cover" priority fetchPriority="high" />

        <Banner bannerSize={resolvedBannerSize} data={slides[0]?.fields} priority />
      </section>
    )
  }

  return (
    <section aria-roledescription="carousel" aria-label="Hero banner carousel" className={cn('embla relative flex w-full flex-col', bannerSizeStyles[resolvedBannerSize])}>
      <Image src={CONTENTFUL_ASSETS.HERO_BACKGROUND} alt="Hero background" fill sizes="100vw" priority fetchPriority="high" className="object-top-center -z-10 object-cover" />

      <div className="relative w-full overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex items-stretch">
          {slides?.map((item, index) => (
            <Banner
              key={item?.fields?.name ?? `slide_${index}`}
              bannerSize={resolvedBannerSize}
              data={item?.fields}
              className="embla__slide flex-[0_0_100%]"
              priority={index === 0}
            />
          ))}
        </div>

        <div className="pointer-events-none absolute inset-x-10 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-between desktop:inset-x-0 desktop:mx-auto desktop:flex desktop:max-w-[1360px]">
          <button
            aria-label="Previous slide"
            onClick={() => emblaApi?.scrollPrev()}
            className="pointer-events-auto flex size-10 items-center justify-center rounded-full bg-overlay-light"
          >
            <CaretLeftIcon weight="bold" size={24} className="text-white" />
          </button>
          <button
            aria-label="Next slide"
            onClick={() => emblaApi?.scrollNext()}
            className="pointer-events-auto flex size-10 items-center justify-center rounded-full bg-overlay-light"
          >
            <CaretRightIcon weight="bold" size={24} className="text-white" />
          </button>
        </div>
      </div>

      {slides.length > 0 && (
        <div className="embla__dots z-10 flex w-full shrink-0 items-center justify-center gap-x-0 pb-2 hover:cursor-pointer lg:absolute lg:inset-x-0 lg:bottom-6 lg:pb-0">
          {slides.map((_, index) => {
            const isActive = currentSlide === index

            return (
              <button
                aria-label={`Go to slide ${index + 1}`}
                aria-current={isActive}
                key={`embla-dot-${index}`}
                className="group flex items-center justify-center px-1.5 py-4"
                onClick={() => handleThumbnailClick(index)}
              >
                <div
                  className={cn('h-0.5 transition-all', isActive ? 'w-8 bg-white opacity-100' : 'w-[30px] border-none bg-white opacity-30 group-hover:w-8 group-hover:opacity-55')}
                />
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}
